/**
 * Nächster Schritt und Befunde je Abschnitt, gelesen aus dem Bericht
 * (`report.ts`). Gerechnet wird auf dem Server
 * (`supabase/functions/_shared/sim/befunde.ts`); hier wird nur geprüft und in
 * Sätze gebracht.
 *
 * ÄLTERE BERICHTE tragen keines der Felder. Dann liefert jede Funktion null
 * oder eine leere Liste, und die Seite zeigt, was sie vorher zeigte. Das
 * Layout des Berichts wird gerade neu entworfen; die Darstellung hier ist
 * bewusst schlicht (eine Zeile und eine Tabelle je Fassung).
 */

import { t } from './sprache';

export interface SchrittAnteile {
  n: number;
  ja: number;
  vielleicht: number;
  nein: number;
  anteil: { ja: number; vielleicht: number; nein: number };
}

export interface SchrittBilanz extends SchrittAnteile {
  basis: 'alle' | 'kern';
  kern?: SchrittAnteile;
  rand?: SchrittAnteile;
}

export interface Stimme { persona: string; text: string }
export interface Befund { n: number; kern?: number; stimmen: Stimme[] }
export interface AbschnittBefund {
  abschnitt: string;
  personas: number;
  von: number;
  kern?: number;
  kern_von?: number;
  wirkt: Befund;
  bremst: Befund;
  fehlt: Befund;
}

const ganz = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const text = (v: unknown): string => (typeof v === 'string' ? v : '');

function leseAnteile(x: any): SchrittAnteile | null {
  if (!x || !ganz(x.n) || !x.n || !ganz(x.ja) || !ganz(x.vielleicht) || !ganz(x.nein)) return null;
  const a = x.anteil ?? {};
  const pct = (v: unknown, z: number) => (ganz(v) ? v : Math.round((100 * z) / x.n));
  return { n: x.n, ja: x.ja, vielleicht: x.vielleicht, nein: x.nein, anteil: { ja: pct(a.ja, x.ja), vielleicht: pct(a.vielleicht, x.vielleicht), nein: pct(a.nein, x.nein) } };
}

export function leseSchritt(x: any): SchrittBilanz | null {
  const alle = leseAnteile(x);
  if (!alle) return null;
  const kern = leseAnteile(x.kern);
  const rand = leseAnteile(x.rand);
  return { ...alle, basis: x.basis === 'kern' && kern ? 'kern' : 'alle', ...(kern ? { kern } : {}), ...(rand ? { rand } : {}) };
}

/** „74 % ja, 13 % vielleicht, 13 % nein (Kern, 8 Urteile)". Folgt dem Kern, wenn es ihn gibt. */
export function schrittSatz(b: SchrittBilanz): string {
  const s = b.basis === 'kern' && b.kern ? b.kern : b;
  return t(
    `${s.anteil.ja} % ja, ${s.anteil.vielleicht} % vielleicht, ${s.anteil.nein} % nein (${b.basis === 'kern' ? 'Kern, ' : ''}${s.n} ${s.n === 1 ? 'Urteil' : 'Urteile'})`,
    `${s.anteil.ja}% yes, ${s.anteil.vielleicht}% maybe, ${s.anteil.nein}% no (${b.basis === 'kern' ? 'core, ' : ''}${s.n} ${s.n === 1 ? 'verdict' : 'verdicts'})`,
  );
}

function leseBefund(x: any): Befund {
  return {
    n: ganz(x?.n) ? x.n : 0,
    ...(ganz(x?.kern) ? { kern: x.kern } : {}),
    stimmen: (Array.isArray(x?.stimmen) ? x.stimmen : [])
      .map((s: any) => ({ persona: text(s?.persona), text: text(s?.text) }))
      .filter((s: Stimme) => s.text)
      .slice(0, 2),
  };
}

export function leseAbschnitte(x: any): AbschnittBefund[] {
  if (!Array.isArray(x)) return [];
  return x
    .filter((a: any) => text(a?.abschnitt) && ganz(a?.personas))
    .map((a: any) => ({
      abschnitt: text(a.abschnitt),
      personas: a.personas,
      von: ganz(a.von) ? a.von : a.personas,
      ...(ganz(a.kern) ? { kern: a.kern } : {}),
      ...(ganz(a.kern_von) ? { kern_von: a.kern_von } : {}),
      wirkt: leseBefund(a.wirkt),
      bremst: leseBefund(a.bremst),
      fehlt: leseBefund(a.fehlt),
    }));
}

/**
 * Was eine Fassung im Bericht dazu trägt. Im Vergleich die geprüfte Fassung
 * und die Vergleichsfassung, im Feedback eine. Leer, wenn nichts da ist.
 */
export function befundeDerFassung(v: any): Array<{ titel: string; schritt: SchrittBilanz | null; abschnitte: AbschnittBefund[] }> {
  if (v?.mode === 'feedback') {
    const schritt = leseSchritt(v.naechster_schritt);
    const abschnitte = leseAbschnitte(v.abschnitte);
    return schritt || abschnitte.length ? [{ titel: t('Diese Fassung', 'This version'), schritt, abschnitte }] : [];
  }
  const out: Array<{ titel: string; schritt: SchrittBilanz | null; abschnitte: AbschnittBefund[] }> = [];
  for (const [key, titel] of [['candidate', t('Geprüfte Fassung', 'Tested version')], ['baseline', t('Vergleichsfassung', 'Baseline')]] as const) {
    const schritt = leseSchritt(v?.naechster_schritt?.[key]);
    const abschnitte = leseAbschnitte(v?.abschnitte?.[key]);
    if (schritt || abschnitte.length) out.push({ titel, schritt, abschnitte });
  }
  return out;
}

/** Zahl in der Tabelle: „3", mit Kern „3 (Kern 2)". 0 als Strich. */
export function befundZahl(b: Befund): string {
  if (!b.n) return '–';
  return b.kern != null ? `${b.n} (${t('Kern', 'core')} ${b.kern})` : String(b.n);
}
